import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { BellPlus, Loader2 } from "lucide-react";

const LABELS = {
  q: "Search", county: "County", town: "Town", property_type: "Type",
  listing_type: "Category", currency: "Currency", min_price: "Min price",
  max_price: "Max price", nights: "Nights", addons: "Amenities",
  institution: "Institution", max_distance: "Max km", gender_pref: "Gender", amenities: "Amenities",
};

function summarize(params) {
  const parts = [];
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;
    if (k === "category") return;
    const val = Array.isArray(v) ? v.join(", ") : String(v);
    parts.push(`${LABELS[k] || k}: ${val}`);
  });
  return parts;
}

export function SaveSearchDialog({ params, source = "home", testId = "save-search" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const chips = summarize(params);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/saved-searches", { email: email.trim(), filters: params, source });
      toast.success("Alert saved — we'll email you when new matches appear.");
      setOpen(false);
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save alert");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`${testId}-trigger`}
          type="button"
          variant="outline"
          className="rounded-full border-[#2C3830]/20 bg-white text-[#2C3830]"
        >
          <BellPlus className="mr-1.5 h-4 w-4" /> Save alert
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle>Get alerts for this search</DialogTitle>
          <DialogDescription>
            We'll email you when new listings match your filters. Unsubscribe anytime.
          </DialogDescription>
        </DialogHeader>
        {chips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((c, i) => (
              <span key={i} className="rounded-full bg-[#F7F5F0] px-2.5 py-1 text-xs text-[#2C3830]">{c}</span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#6B7280]">You'll be alerted about all new listings.</p>
        )}
        <form onSubmit={submit} className="mt-2 space-y-3">
          <div className="space-y-1.5">
            <Label className="label-caps text-[#6B7280]">Email</Label>
            <Input
              data-testid={`${testId}-email`}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button
              data-testid={`${testId}-submit`}
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save alert"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
